/**
 * edj-bundle-carousel.js
 * « Complétez le rituel » — carousel multi-packs + add-multiple-to-cart
 * VAGUE 8 — EDJ Shopify theme
 *
 * Architecture :
 *   - 1 à 3 packs par produit (issus du metaobject `bundle_pack`)
 *   - Carousel : show/hide via class `.is-active`, navigation prev/next + dots
 *   - Calcul total prix par pack au load + sur change variant (select)
 *   - "Tout ajouter au panier" → POST /cart/add.js avec items[]
 *
 * Pixel-perfect references :
 *   - desktop : page-produit-a-desktop.jsx 290-323
 *   - mobile  : page-produit-a-mobile.jsx 372-396
 *
 * Self-registering, idempotent (guard via window.__edjBundleCarouselInit + dataset.bound).
 *
 * Note : on travaille en cents (Shopify variant.price renvoie cents).
 *        Format monétaire : Intl.NumberFormat fr-FR EUR.
 */
(function () {
  'use strict';

  if (window.__edjBundleCarouselInit) return;
  window.__edjBundleCarouselInit = true;

  const moneyFormatter = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR',
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });

  function formatMoneyCents(cents) {
    if (!Number.isFinite(cents)) return '';
    return moneyFormatter.format(cents / 100);
  }

  function getCardPriceCents(card) {
    if (!card) return 0;
    const select = card.querySelector('[data-bundle-card-variant]');
    if (select) {
      const opt = select.options[select.selectedIndex];
      const v = opt && parseInt(opt.dataset.priceCents || '0', 10);
      if (Number.isFinite(v) && v > 0) return v;
    }
    const v = parseInt(card.dataset.priceCents || '0', 10);
    return Number.isFinite(v) ? v : 0;
  }

  function getCardVariantId(card) {
    if (!card) return null;
    const select = card.querySelector('[data-bundle-card-variant]');
    if (select && select.value) return parseInt(select.value, 10);
    const v = parseInt(card.dataset.variantId || '0', 10);
    return Number.isFinite(v) && v > 0 ? v : null;
  }

  function recalcPackTotal(packEl, defaultDiscount) {
    const cards = packEl.querySelectorAll('[data-bundle-card]');
    const discountPct = parseFloat(packEl.dataset.packDiscount || defaultDiscount || '0') || 0;

    let sum = 0;
    cards.forEach((card) => {
      sum += getCardPriceCents(card);
    });

    const saving = Math.round(sum * (discountPct / 100));
    const final = sum - saving;

    const oldEl = packEl.querySelector('[data-pack-old]');
    const newEl = packEl.querySelector('[data-pack-new]');
    const savingEl = packEl.querySelector('[data-pack-saving]');

    if (newEl) newEl.textContent = formatMoneyCents(final);
    if (oldEl) {
      if (saving > 0) {
        oldEl.textContent = formatMoneyCents(sum);
        oldEl.style.display = '';
      } else {
        oldEl.textContent = '';
        oldEl.style.display = 'none';
      }
    }
    if (savingEl) savingEl.textContent = formatMoneyCents(saving);
  }

  function updateCardPriceDisplay(card) {
    const priceEl = card.querySelector('[data-bundle-card-price]');
    if (!priceEl) return;
    priceEl.textContent = formatMoneyCents(getCardPriceCents(card));
  }

  function setFeedback(packEl, message, state) {
    const fb = packEl.querySelector('[data-pack-feedback]');
    if (!fb) return;
    fb.textContent = message || '';
    if (state) fb.dataset.state = state;
    else delete fb.dataset.state;
  }

  function setActivePack(carousel, targetIndex) {
    // IMPORTANT : sélecteur restreint aux <article> packs.
    // Le bouton CTA porte aussi data-pack-index (pour identifier le pack lors
    // de l'add-to-cart) — on doit l'exclure du compteur de slides, sinon le
    // carousel boucle sur des slides phantom (pack count × 2).
    const packs = carousel.querySelectorAll('.edj-bundle__pack[data-pack-index]');
    const dots = carousel.parentElement
      ? carousel.parentElement.querySelectorAll('[data-edj-bundle-dot]')
      : carousel.querySelectorAll('[data-edj-bundle-dot]');
    // Section-level dots are siblings (in <nav>), look in section
    const section = carousel.closest('[data-edj-bundle-section]');
    const sectionDots = section ? section.querySelectorAll('[data-edj-bundle-dot]') : dots;

    const total = packs.length;
    const idx = ((targetIndex % total) + total) % total; // wrap

    packs.forEach((p) => {
      const pIdx = parseInt(p.dataset.packIndex, 10);
      if (pIdx === idx) {
        p.classList.add('is-active');
        p.removeAttribute('aria-hidden');
      } else {
        p.classList.remove('is-active');
        p.setAttribute('aria-hidden', 'true');
      }
    });
    sectionDots.forEach((d) => {
      const dIdx = parseInt(d.dataset.edjBundleDot, 10);
      if (dIdx === idx) {
        d.classList.add('is-active');
        d.setAttribute('aria-selected', 'true');
      } else {
        d.classList.remove('is-active');
        d.setAttribute('aria-selected', 'false');
      }
    });

    carousel.dataset.activeIndex = String(idx);
  }

  async function addPackToCart(packEl, ctaBtn) {
    const cards = packEl.querySelectorAll('[data-bundle-card]');
    const items = [];
    cards.forEach((card) => {
      const id = getCardVariantId(card);
      if (id) items.push({ id, quantity: 1 });
    });

    if (items.length === 0) {
      setFeedback(packEl, 'Aucun produit disponible.', 'error');
      return;
    }

    const carousel = packEl.closest('[data-edj-bundle-carousel]');
    const ctaDefault = (carousel && carousel.dataset.ctaDefault) || 'Tout ajouter au panier';
    const ctaLoading = (carousel && carousel.dataset.ctaLoading) || 'Ajout en cours…';
    const ctaSuccess = (carousel && carousel.dataset.ctaSuccess) || 'Ajouté au panier ✓';
    const previousLabel = ctaBtn.textContent;

    ctaBtn.disabled = true;
    ctaBtn.textContent = ctaLoading;
    setFeedback(packEl, '', null);

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        let errMsg = 'Impossible d\'ajouter le pack au panier.';
        try {
          const data = await res.json();
          if (data && data.description) errMsg = data.description;
          else if (data && data.message) errMsg = data.message;
        } catch (_) {}
        throw new Error(errMsg);
      }

      const data = await res.json();
      ctaBtn.textContent = ctaSuccess;
      setFeedback(packEl, 'Pack ajouté au panier (' + items.length + ' articles).', 'success');

      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { source: 'product-bundle', added: data },
      }));
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        bubbles: true,
        detail: { source: 'product-bundle', items },
      }));

      setTimeout(() => {
        ctaBtn.textContent = previousLabel || ctaDefault;
        ctaBtn.disabled = false;
      }, 2200);
    } catch (err) {
      ctaBtn.textContent = previousLabel || ctaDefault;
      ctaBtn.disabled = false;
      setFeedback(packEl, err.message || 'Erreur d\'ajout au panier.', 'error');
    }
  }

  function initCarousel(carousel) {
    if (carousel.dataset.bound === '1') return;
    carousel.dataset.bound = '1';

    const section = carousel.closest('[data-edj-bundle-section]');
    const defaultDiscount = parseFloat(carousel.dataset.defaultDiscount || '0') || 0;
    // Sélecteur restreint aux <article> (cf. setActivePack) — exclut le CTA
    // qui porte aussi data-pack-index.
    const packs = carousel.querySelectorAll('.edj-bundle__pack[data-pack-index]');

    // 1. Initial recalc per pack + bind variant change
    packs.forEach((packEl) => {
      const cards = packEl.querySelectorAll('[data-bundle-card]');
      cards.forEach((card) => {
        const select = card.querySelector('[data-bundle-card-variant]');
        if (select) {
          select.addEventListener('change', () => {
            const opt = select.options[select.selectedIndex];
            if (opt) card.dataset.priceCents = opt.dataset.priceCents || '0';
            card.dataset.variantId = select.value;
            updateCardPriceDisplay(card);
            recalcPackTotal(packEl, defaultDiscount);
          });
        }
      });
      recalcPackTotal(packEl, defaultDiscount);

      // Bind CTA
      const cta = packEl.querySelector('[data-pack-add-to-cart]');
      if (cta) {
        cta.addEventListener('click', (ev) => {
          ev.preventDefault();
          addPackToCart(packEl, cta);
        });
      }
    });

    // 2. Carousel navigation (only if >1 pack)
    if (packs.length > 1 && section) {
      const prev = section.querySelector('[data-edj-bundle-prev]');
      const next = section.querySelector('[data-edj-bundle-next]');
      const dots = section.querySelectorAll('[data-edj-bundle-dot]');

      const getActive = () => parseInt(carousel.dataset.activeIndex || '0', 10) || 0;

      if (prev) {
        prev.addEventListener('click', () => setActivePack(carousel, getActive() - 1));
      }
      if (next) {
        next.addEventListener('click', () => setActivePack(carousel, getActive() + 1));
      }
      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.edjBundleDot, 10);
          if (Number.isFinite(idx)) setActivePack(carousel, idx);
        });
      });

      // Init active index
      carousel.dataset.activeIndex = '0';
    }
  }

  /**
   * Hydratation async des recommendations (cas 3 : pas de packs, placeholder host).
   * Fetch /recommendations/products?... et remplace le contenu de la section
   * par celui retourné. Si la réponse ne contient aucun produit ou erreur,
   * la section est masquée pour rester invisible.
   *
   * Note sécurité : la réponse provient du même origin Shopify (trusted endpoint),
   * et nous utilisons DOMParser pour parser sans exécuter de scripts inline.
   */
  async function hydrateRecommendations(host) {
    if (host.dataset.bound === '1') return;
    host.dataset.bound = '1';

    const productId = host.dataset.productId;
    const sectionId = host.dataset.sectionId;
    const intent = host.dataset.intent || 'complementary';
    const section = host.closest('[data-edj-bundle-section]');

    if (!productId || !sectionId || !section) return;

    const url = '/recommendations/products?section_id=' + encodeURIComponent(sectionId)
      + '&product_id=' + encodeURIComponent(productId)
      + '&intent=' + encodeURIComponent(intent)
      + '&limit=2';

    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('reco fetch failed: ' + res.status);
      const html = await res.text();

      // DOMParser avoids executing inline scripts and is safer than innerHTML
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newSection = doc.querySelector('[data-edj-bundle-section]');
      const newCarousel = newSection && newSection.querySelector('[data-edj-bundle-carousel]');

      if (!newCarousel) {
        // Soit Search & Discovery non installée, soit pas de produits → on masque
        section.remove();
        return;
      }

      // Remplace la section actuelle par la version hydratée
      section.replaceWith(newSection);
      scan(newSection);
    } catch (err) {
      // En cas d'erreur réseau ou parsing, on masque la section pour rester graceful
      section.remove();
    }
  }

  function scan(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-edj-bundle-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-edj-bundle-recommendations-host]').forEach(hydrateRecommendations);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document));
  } else {
    scan(document);
  }

  // Re-scan on Shopify section reload (Theme Editor)
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target) scan(e.target);
  });
})();
